import { useRef } from "react";
import { AnimatePresence, motion, useInView, useScroll, useTransform } from "framer-motion";
import { Infinity as InfinityIcon, Magnet, Minimize2, Smartphone } from "lucide-react";
import { SUBLINE, TAGLINE } from "@/data/site";
import { PrimaryButton, SecondaryButton } from "@/components/Primitives";
import HeroVisual from "@/components/HeroVisual";
import PlatformSwitcher from "@/components/PlatformSwitcher";
import { usePlatform } from "@/context/PlatformContext";
import { useCountry } from "@/context/CountryContext";
import { PACKS, packPrice } from "@/data/products";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const CHIPS = [
  { icon: InfinityIcon, label: "Unlimited Range", highlight: true },
  { icon: Magnet, label: "Magnetic" },
  { icon: Minimize2, label: "Compact" },
  { icon: Smartphone, label: null, dynamic: "network" }, // "Works with Apple Find My" / "Works with Google Find Hub"
];

const Hero = () => {
  const ref = useRef(null);
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { margin: "100px 0px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const pointer = useRef({ x: 0, y: 0, active: false });
  const { config: platformCfg, platform } = usePlatform();
  const { market, symbol, currency } = useCountry();
  const smallestPackPrice = Math.min(...PACKS.map((p) => packPrice(p, market)));

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    pointer.current = {
      x: (e.clientX - r.left) / r.width - 0.5,
      y: (e.clientY - r.top) / r.height - 0.5,
      active: true,
    };
  };
  const onLeave = () => {
    pointer.current = { x: 0, y: 0, active: false };
  };

  return (
    <section id="top" ref={ref} className="noise-overlay relative overflow-hidden">
      <div
        className="pointer-events-none absolute -top-32 right-0 h-[480px] w-[480px] rounded-full blur-[120px] transition-colors duration-700"
        style={{ backgroundColor: "var(--brand-accent-soft)" }}
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 pb-14 pt-8 sm:gap-12 sm:px-6 sm:pb-16 sm:pt-10 lg:grid-cols-12 lg:px-8 lg:pb-28 lg:pt-20">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-5"
          >
            <PlatformSwitcher testIdPrefix="hero-platform-switcher" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-tracksy-sub shadow-subtle"
          >
            <span className="h-1.5 w-1.5 rounded-full transition-colors duration-500" style={{ backgroundColor: "var(--brand-accent)" }} />
            <AnimatePresence mode="wait">
              <motion.span
                key={platform}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.35 }}
              >
                {platformCfg.heroEyebrow}
              </motion.span>
            </AnimatePresence>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-heading text-4xl font-extrabold leading-[1.04] tracking-tight sm:text-5xl lg:text-[4.6rem]">
            <MaskedLine delay={0.1}>Track What</MaskedLine>
            <MaskedLine delay={0.24}>
              <span style={{ color: "var(--brand-accent)" }} className="transition-colors duration-500">Matters.</span>
            </MaskedLine>
          </h1>

          <AnimatePresence mode="wait">
            <motion.p
              key={platform}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="mt-6 max-w-lg text-base leading-relaxed text-tracksy-sub sm:text-lg"
            >
              Unlimited range. Zero subscriptions. Magnetic by design. The compact {platformCfg.networkShort} tracker built for your car, trailer, tools and every valuable thing in between.
            </motion.p>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.64 }}
            className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4"
          >
            <PrimaryButton to="/shop" testId="hero-shop-cta" className="w-full sm:w-auto">Shop Tracksy</PrimaryButton>
            <SecondaryButton to="/fleet" testId="hero-fleet-cta" className="w-full sm:w-auto">Explore Tracksy Fleet</SecondaryButton>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            data-testid="hero-chips"
            className="mt-8 flex flex-wrap gap-2"
          >
            {CHIPS.map((c) => {
              const label = c.dynamic === "network" ? `Works with ${platformCfg.network}` : c.label;
              return (
              <li
                key={label}
                className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors duration-500 ${
                  c.highlight
                    ? "border"
                    : "bg-tracksy-cream text-tracksy-ink"
                }`}
                style={c.highlight ? {
                  background: "var(--brand-accent-soft)",
                  borderColor: "var(--brand-accent-ring)",
                  color: "var(--brand-accent)",
                  boxShadow: "var(--brand-accent-glow)",
                } : undefined}
              >
                <c.icon className="h-3.5 w-3.5" style={{ color: "var(--brand-accent)" }} />
                {label}
              </li>
            );})}
          </motion.ul>
        </div>

        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: parallaxY }}
          >
            <div
              ref={cardRef}
              data-testid="hero-product-card"
              onPointerMove={onMove}
              onPointerLeave={onLeave}
              className="relative overflow-hidden rounded-[2rem] bg-tracksy-cream shadow-lift"
            >
              <div
                className="absolute inset-0 transition-colors duration-700"
                style={{ background: "radial-gradient(ellipse at 60% 35%, var(--brand-accent-soft), transparent 60%)" }}
              />
              <div data-testid="hero-3d-canvas" className="relative flex aspect-square w-full items-center justify-center p-5 sm:aspect-[5/4] sm:p-8 lg:aspect-[4/5]">
                <HeroVisual />
              </div>

              <div className="pointer-events-none absolute left-4 top-4 hidden rounded-full border border-black/5 bg-white/85 px-3.5 py-2 text-xs font-bold text-tracksy-ink backdrop-blur-xl sm:left-auto sm:right-5 sm:top-5 sm:inline-flex">
                Live on <AnimatePresence mode="wait"><motion.span key={platform} initial={{opacity:0,y:4}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-4}} transition={{duration:0.3}} style={{ color: "var(--brand-accent)" }}>&nbsp;{platformCfg.network}</motion.span></AnimatePresence>
              </div>
              <div className="pointer-events-none absolute bottom-4 left-4 rounded-full bg-tracksy-ink px-3.5 py-2 text-xs font-bold text-white sm:bottom-5 sm:left-auto sm:right-5">
                From {symbol}{smallestPackPrice} {currency}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
